import { useEffect, useState } from 'react';

const HEARTBEAT_INTERVAL_MS = 15000;
const HEARTBEAT_TIMEOUT_MS = 5000;

/**
 * navigator.onLine only reflects whether the OS network interface is up --
 * it stays true on a captive portal or a VPN with no route to the API, and
 * that's exactly the kind of "looks connected but isn't" situation clinics
 * hit. Back it with a real reachability check against the backend's
 * unauthenticated /health/ endpoint (backend/config/urls.py) rather than
 * going through apiClient, so this never gets tangled up in the JWT
 * refresh interceptor.
 */
function getHealthUrl(): string {
  const base = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000')
    .replace(/\/+$/, '')
    .replace(/\/api$/, '');
  return `${base}/health/`;
}

async function pingServer(): Promise<boolean> {
  if (!navigator.onLine) {
    return false;
  }
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HEARTBEAT_TIMEOUT_MS);
  try {
    const response = await fetch(getHealthUrl(), {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const reachable = await pingServer();
      if (!cancelled) {
        setIsOnline(reachable);
      }
    };

    check();
    const intervalId = setInterval(check, HEARTBEAT_INTERVAL_MS);

    const handleOffline = () => {
      if (!cancelled) {
        setIsOnline(false);
      }
    };
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', check);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', check);
    };
  }, []);

  return isOnline;
}
